import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { NgClass } from '@angular/common';
import { FpLoggerNoiseSpreadStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spread/services/fp-logger-noise-spread/fp-logger-noise-spread.store';
import { FpLoggerNoiseSpreadChartService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spread/services/fp-logger-noise-spread-chart.service';
import { FpLoggerNoiseSpreadExportService } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spread/services/fp-logger-noise-spread-export.service';
import { FpLoggerNoiseSpreadActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spread/services/fp-logger-noise-spread/fp-logger-noise-spread.actions';
import { FloatingPopupSelectDateRangeActionComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-select-date-range-action/floating-popup-select-date-range-action.component';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import { MonthsDateRangeType } from '@app/core/date-range/enums/months-date-range-type.enum';
import { FormControlConfigType } from '@app/core/enums/form-control-config-type';
import { monthsDateRangeTypeLocalize } from '@app/core/date-range/localize/months-date-range-type';
import { FloatingPopupBarChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-bar-chart-wrapper/floating-popup-bar-chart-wrapper.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-logger-noise-spread',
  standalone: true,
  imports: [
    ElementWidthClassDirective,
    FloatingPopupBarChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FloatingPopupSelectDateRangeActionComponent,
    FloatingPopupBaseErrorMessageComponent,
    NgClass,
  ],
  templateUrl: './fp-logger-noise-spread.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpLoggerNoiseSpreadComponent {
  protected readonly screenResolutionService = inject(ScreenResolutionService);
  public readonly currentTabStore = inject(FpLoggerNoiseSpreadStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpLoggerNoiseSpreadChartService);
  protected readonly fpLoggerNoiseSpreadExportService = inject(FpLoggerNoiseSpreadExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);

  public popup = input<IFloatingPopupInstance>();

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly loggerDeployment = computed(() => this.popupMainData()?.deployment);

  public combinedChartData = computed(() => {
    const data = this.data() || [];

    const chartData = this.chartService.getDataForChart(data);
    const chartOptions = this.chartService.generateOptions();

    return { chartData, chartOptions };
  });

  public rangeSelection: IEnumFormControlConfig<MonthsDateRangeType> = {
    id: `select-date-range-${this.popup()?.id}`,
    type: FormControlConfigType.Enum,
    values: [
      MonthsDateRangeType.One,
      MonthsDateRangeType.Three,
      MonthsDateRangeType.Six,
      MonthsDateRangeType.Twelve,
      MonthsDateRangeType.TwentyFor,
      MonthsDateRangeType.FullHistory,
    ],
    defaultValue: MonthsDateRangeType.Six,
    localize: monthsDateRangeTypeLocalize,
    getValue: () => this.currentTabStore.period$,
    valueChange: value => {
      this.currentTabStore.dispatch({
        type: FpLoggerNoiseSpreadActionType.UpdatePeriod,
        period: value,
      });
    },
  };

  public doExport() {
    const popupMainData = this.popupMainData();

    if (!popupMainData) {
      return;
    }

    const data = this.data() || [];

    this.fpLoggerNoiseSpreadExportService.exportCsv(popupMainData, data);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpLoggerNoiseSpreadActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
